// features/videos/real/useRealVideos.ts — small fetch hooks for the real-data Videos page.
// Each keeps its own state; nothing here is shared app-wide yet, so no store. Results are
// stored WITH the key they were fetched for, and anything fetched for another key reads as
// "loading" — so a chip or video change never flashes the previous list, and no effect has
// to reset state synchronously.

import { useCallback, useEffect, useState } from 'react';
import type { Post } from '@/core/models/post';
import { postsService } from '@/core/services/postsService';
import {
  type UpNextMode,
  videoFeedService,
  type VideoFilter,
  type VideosHome,
} from '@/core/services/videoFeedService';

export type Loadable<T> = { state: 'loading' } | { state: 'error' } | { state: 'ready'; data: T };

export function useVideosHome(): Loadable<VideosHome> {
  const [value, setValue] = useState<Loadable<VideosHome>>({ state: 'loading' });
  useEffect(() => {
    let live = true;
    videoFeedService
      .home()
      .then((data) => live && setValue({ state: 'ready', data }))
      .catch(() => live && setValue({ state: 'error' }));
    return () => {
      live = false;
    };
  }, []);
  return value;
}

const PAGE = 24;

interface ListResult {
  filter: VideoFilter;
  posts: Post[];
  state: 'ready' | 'error';
  done: boolean;
}

/** One chip's list, with "load more" by the last item's createdAt. */
export function useVideoList(filter: VideoFilter | null) {
  const [result, setResult] = useState<ListResult | null>(null);

  useEffect(() => {
    if (!filter) return;
    let live = true;
    videoFeedService
      .list(filter, { limit: PAGE })
      .then((posts) => {
        if (live)
          setResult({
            filter,
            posts,
            state: 'ready',
            done: posts.length < PAGE || filter === 'trending',
          });
      })
      .catch(() => {
        if (live) setResult({ filter, posts: [], state: 'error', done: true });
      });
    return () => {
      live = false;
    };
  }, [filter]);

  const current = result && result.filter === filter ? result : null;

  const loadMore = useCallback(async () => {
    const last = current?.posts[current.posts.length - 1];
    if (!filter || !current || current.done || !last?.createdAt) return;
    const rows = await videoFeedService.list(filter, { before: last.createdAt, limit: PAGE });
    setResult((prev) =>
      prev && prev.filter === filter
        ? { ...prev, posts: [...prev.posts, ...rows], done: rows.length < PAGE }
        : prev,
    );
  }, [filter, current]);

  return {
    posts: current?.posts ?? [],
    state: !filter ? ('idle' as const) : (current?.state ?? ('loading' as const)),
    done: current?.done ?? false,
    loadMore,
  };
}

export function useWatchLater(enabled: boolean) {
  const [posts, setPosts] = useState<Post[] | null>(null);
  const reload = useCallback(() => {
    if (!enabled) return;
    videoFeedService
      .watchLater()
      .then(setPosts)
      .catch(() => setPosts([]));
  }, [enabled]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { posts, reload };
}

export function useUpNext(postId: string | undefined, mode: UpNextMode = 'for_you'): Post[] | null {
  const key = postId ? `${postId}|${mode}` : null;
  const [result, setResult] = useState<{ key: string; posts: Post[] } | null>(null);
  useEffect(() => {
    if (!postId || !key) return;
    let live = true;
    videoFeedService
      .upNext(postId, mode)
      .then((posts) => live && setResult({ key, posts }))
      .catch(() => live && setResult({ key, posts: [] }));
    return () => {
      live = false;
    };
  }, [postId, mode, key]);
  return result && result.key === key ? result.posts : null;
}

/**
 * The real Shorts the vertical player swipes through: the newest Shorts, with the one in
 * the URL guaranteed to be present (an older Short opened from a link is fetched on its
 * own and put first). `active` is false for a sample id, which keeps its seed player.
 */
export function useRealShorts(active: boolean, currentId: string | undefined) {
  const [result, setResult] = useState<{ key: string; posts: Post[] } | null>(null);
  const key = active && currentId ? currentId : '';
  useEffect(() => {
    if (!key) return;
    let live = true;
    void (async () => {
      let posts = await videoFeedService.list('shorts', { limit: 48 }).catch((): Post[] => []);
      if (!posts.some((p) => p.id === key)) {
        const one = await postsService.getPost(key);
        if (one?.video?.isShort) posts = [one, ...posts];
      }
      if (live) setResult({ key, posts });
    })();
    return () => {
      live = false;
    };
  }, [key]);
  const current = result && result.key === key ? result : null;
  return { posts: current?.posts ?? [], loaded: Boolean(current) };
}
